<div class="analytics-container">
  <div class="filter-header" [formGroup]="filterForm">
    <h2>Dashboard Insights</h2>
    <div class="quick-filters">
      <button mat-stroked-button
              type="button"
              [class.active]="activeQuickFilter === 'day'"
              (click)="setQuickFilter('day')">Last day</button>
      <button mat-stroked-button
              type="button"
              [class.active]="activeQuickFilter === 'week'"
              (click)="setQuickFilter('week')">Last week</button>
      <button mat-stroked-button
              type="button"
              [class.active]="activeQuickFilter === 'month'"
              (click)="setQuickFilter('month')">Last month</button>
      <button mat-stroked-button
              type="button"
              [class.active]="activeQuickFilter === 'custom'"
              (click)="setQuickFilter('custom')">Custom</button>
    </div>
    <div class="date-picker-range" *ngIf="activeQuickFilter === 'custom'">
      <mat-form-field appearance="outline">
        <mat-label>Enter a date range</mat-label>
        <mat-date-range-input [rangePicker]="picker" [formGroup]="filterForm">
          <input matStartDate formControlName="fromDate" placeholder="Start date">
          <input matEndDate formControlName="toDate" placeholder="End date">
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker (opened)="onCustomRangeOpened()"></mat-date-range-picker>
      </mat-form-field>
    </div>
  </div>

  <app-challenge-walkthrough-measurements
    [fromDate]="filterForm.value.fromDate?.toISOString()"
    [toDate]="filterForm.value.toDate?.toISOString()" />

  <div class="loading-overlay" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading analytics...</p>
  </div>

  <div class="dashboard-grid" *ngIf="!loading && analyticsData">
    <!-- Critical: user health -->
    <div class="overview-section">
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Total users</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.overview.totalUsers }}
        </mat-card-content>
        <div class="card-sub">Registered, non-deleted</div>
      </mat-card>

      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>New users</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.overview.newUsers }}
        </mat-card-content>
        <div class="card-sub">Created in range</div>
      </mat-card>

      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Active users</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.overview.activeUsers }}
        </mat-card-content>
        <div class="card-sub">Period-active</div>
      </mat-card>

      <mat-card class="stats-card warn">
        <mat-card-header>
          <mat-card-title>Deleted users</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.overview.deletedUsersPeriod }}
        </mat-card-content>
        <div class="card-sub">Period · {{ analyticsData.overview.deletedUsersTotal }} total</div>
      </mat-card>

      <mat-card class="stats-card" [class.positive]="analyticsData.overview.netGrowth >= 0" [class.warn]="analyticsData.overview.netGrowth < 0">
        <mat-card-header>
          <mat-card-title>Net growth</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.overview.netGrowth >= 0 ? '+' : '' }}{{ analyticsData.overview.netGrowth }}
        </mat-card-content>
        <div class="card-sub">New − deleted (period)</div>
      </mat-card>
    </div>

    <div class="overview-section" *ngIf="analyticsData.challengeFunnel as cf">
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Challenge registered</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">{{ cf.registered }}</mat-card-content>
        <div class="card-sub">Open challenge registrations in range</div>
      </mat-card>
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Submitted</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">{{ cf.submitted }}</mat-card-content>
      </mat-card>
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Scored</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">{{ cf.scored }}</mat-card-content>
      </mat-card>
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Remediation claimed</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">{{ cf.remediationClaimed }}</mat-card-content>
      </mat-card>
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Paid / remaining</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">{{ cf.paidConversion }}</mat-card-content>
        <div class="card-sub">Scored minus remediation (proxy)</div>
      </mat-card>
    </div>

    <!-- Critical: feedback health -->
    <div class="overview-section feedback-kpis">
      <mat-card class="stats-card">
        <mat-card-header>
          <mat-card-title>Avg rating</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.feedback.averageRating | number:'1.1-2' }}
          <span class="unit">/ 5</span>
        </mat-card-content>
        <div class="card-sub">{{ analyticsData.feedback.totalCount }} feedbacks</div>
      </mat-card>

      <mat-card class="stats-card danger">
        <mat-card-header>
          <mat-card-title>Critical / high issues</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.feedback.criticalIssueCount }}
        </mat-card-content>
      </mat-card>

      <mat-card class="stats-card danger">
        <mat-card-header>
          <mat-card-title>Low ratings (≤2)</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.feedback.lowRatingCount }}
        </mat-card-content>
      </mat-card>

      <mat-card class="stats-card danger">
        <mat-card-header>
          <mat-card-title>NPS detractors (≤6)</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val">
          {{ analyticsData.feedback.npsDetractorCount }}
        </mat-card-content>
        <div class="card-sub" *ngIf="analyticsData.feedback.averageNps != null">
          Avg NPS {{ analyticsData.feedback.averageNps | number:'1.1-2' }}
        </div>
      </mat-card>

      <mat-card class="stats-card paid">
        <mat-card-header>
          <mat-card-title>Revenue</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val currency">
          {{ analyticsData.overview.totalRevenue | currency:'INR':'symbol':'1.0-0' }}
        </mat-card-content>
      </mat-card>

      <mat-card class="stats-card expense">
        <mat-card-header>
          <mat-card-title>AI cost</mat-card-title>
        </mat-card-header>
        <mat-card-content class="numeric-val currency">
          {{ analyticsData.overview.totalAICost | currency:'USD':'symbol':'1.2-2' }}
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Critical feedback strip -->
    <mat-card class="critical-strip-card">
      <mat-card-header>
        <mat-card-title>Critical feedback snapshot</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="critical-strip" *ngIf="analyticsData.feedback.criticalItems.length; else noCritical">
          <div class="critical-item" *ngFor="let item of analyticsData.feedback.criticalItems">
            <div class="critical-meta">
              <span class="reason-badge">{{ toCriticalReasonLabel(item) }}</span>
              <span class="date">{{ item.feedbackDate | date:'short' }}</span>
            </div>
            <div class="critical-main">
              <strong>{{ item.userName || 'Unknown' }}</strong>
              <span class="area">{{ item.appArea }}</span>
              <span class="signal" *ngIf="item.rating != null">Score {{ item.rating }}</span>
              <span class="signal" *ngIf="item.issueCode">{{ item.issueCode }}</span>
            </div>
            <div class="critical-text" *ngIf="item.text">{{ item.text }}</div>
          </div>
        </div>
        <ng-template #noCritical>
          <p class="empty-critical">No critical, low-rating, or NPS-detractor feedback in this range.</p>
        </ng-template>
      </mat-card-content>
    </mat-card>

    <!-- Secondary: Funnel -->
    <div class="funnel-section">
      <mat-card>
        <mat-card-header>
          <mat-card-title>User Conversion Funnel</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="funnel-chart">
            <div class="funnel-step" *ngFor="let step of analyticsData.funnel; let i = index">
              <div class="step-label">{{ step.stepName | titlecase }}</div>
              <div class="step-bar-container">
                <div class="step-bar" [style.width]="(100 - step.dropOffRate) + '%'">
                  <span class="step-count">{{ step.count }} users</span>
                </div>
              </div>
              <div class="drop-off" *ngIf="i > 0">
                Dropoff: <span class="dropoff-val">{{ step.dropOffRate }}%</span>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Secondary: Feedback table & revenue -->
    <div class="details-section">
      <mat-card class="feedback-card">
        <mat-card-header>
          <mat-card-title>Feedback Summary</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="rating-display">
            <mat-icon class="star-icon">star</mat-icon>
            <span class="rating-val">{{ analyticsData.feedback.averageRating | number:'1.1-2' }} / 5</span>
          </div>

          <div class="reactions-row">
            <h4>Reactions</h4>
            <div class="reaction-chips" *ngIf="(analyticsData.feedback.reactions | keyvalue).length; else noReactions">
              <span class="reaction-chip" *ngFor="let r of analyticsData.feedback.reactions | keyvalue">{{ r.key }}: {{ r.value }}</span>
            </div>
            <ng-template #noReactions><p class="muted">No reactions in range</p></ng-template>
          </div>

          <h4>Top Issues Reported</h4>
          <ul>
            <li *ngFor="let issue of analyticsData.feedback.topIssues">{{ issue }}</li>
            <li *ngIf="analyticsData.feedback.topIssues.length === 0">No issues reported</li>
          </ul>

          <h4 class="feedback-table-title">Feedback Received</h4>
          <div class="feedback-filters" [formGroup]="feedbackFilterForm">
            <mat-form-field appearance="outline">
              <mat-label>App Area</mat-label>
              <mat-select formControlName="appArea">
                <mat-option value="">All</mat-option>
                <mat-option *ngFor="let area of appAreaOptions" [value]="area">
                  {{ area }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="search-field">
              <mat-label>Search User</mat-label>
              <input matInput formControlName="searchText" placeholder="Name contains..." />
            </mat-form-field>

            <button mat-stroked-button type="button" (click)="applyFeedbackFilters()">Apply</button>
            <button mat-button type="button" (click)="clearFeedbackFilters()">Reset</button>
          </div>

          <table class="feedback-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Area</th>
                <th>Type</th>
                <th>Signal</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of feedbackRows">
                <td>{{ row.feedbackDate | date:'short' }}</td>
                <td>{{ row.userName }}</td>
                <td>{{ row.appArea }}</td>
                <td>{{ row.type }}</td>
                <td>{{ toFeedbackSignal(row) }}</td>
                <td>{{ row.text || '-' }}</td>
              </tr>
              <tr *ngIf="feedbackRows.length === 0">
                <td colspan="6">No feedback records for selected range</td>
              </tr>
            </tbody>
          </table>

          <div class="feedback-pager">
            <button mat-stroked-button type="button" (click)="loadFeedbackPage(feedbackPageIndex - 1)" [disabled]="feedbackPageIndex <= 1">
              Previous
            </button>
            <span>Page {{ feedbackPageIndex }} / {{ totalFeedbackPages }}</span>
            <button mat-stroked-button type="button" (click)="loadFeedbackPage(feedbackPageIndex + 1)" [disabled]="feedbackPageIndex >= totalFeedbackPages">
              Next
            </button>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="revenue-series-card">
        <mat-card-header>
          <mat-card-title>Daily Revenue Breakdown</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <table class="revenue-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of analyticsData.revenue">
                <td>{{ item.date | date:'mediumDate' }}</td>
                <td>{{ item.amount | currency:'INR' }}</td>
              </tr>
              <tr *ngIf="analyticsData.revenue.length === 0">
                <td colspan="2">No revenue inside range</td>
              </tr>
            </tbody>
          </table>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</div>
